/**
 * Collection Template Script
 * ------------------------------------------------------------------------------
 * A file that contains scripts highly couple code to the Collection template.
 *
 * @namespace collection
 */
import {register} from '@shopify/theme-sections';
import customSelect from 'custom-select';

import ProductCard from '../components/product-card';

register('collection-template', {
  onLoad() {
    let adjustingSrollPosition = false;

    const productCards = this.container.querySelectorAll('.product-card');
    if (productCards && productCards.length > 0) {
      productCards.forEach((product) => ProductCard(product));
    }

    /**
     * Infinite Scroll
     * ------------------------------------------------------------------------------
     * Uses the Shopify Section Rendering API to grab list items from the next or previous collection page
     * for improved performance when infinite scrolling.
     * We build a section URL for our AJAX request. Much better than having to paginate and load another page.
     * https://shopify.dev/docs/themes/sections/section-rendering-api
     */
    const bindingSection = document.querySelector('#shopify-section-template-collection .collection-grid');
    const loadNextIntersect = document.querySelector('[data-infinite-load-next]');
    const loadPrevIntersect = document.querySelector('[data-infinite-load-prev]');
    const loadPrevSpinner = document.querySelector('.spinner-load-prev');
    const loadNextSpinner = document.querySelector('.spinner-load-next');

    /**
     * Product section constructor. Runs on page load as well as Theme Editor
     * `section:load` events.
     * @param {string} sectionID - the section ID to be retrieved
     * @param {element} trigger - element that triggers infinite scroll
     * @param {element} sectionBind - element that the new HTML will get bound to
     */
    function handleAutoInfiniteScroll(sectionID, trigger, sectionBind) {
      if (!trigger) return;
      const observer = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        getPage(sectionID, entries[0], sectionBind);
        observer.unobserve(trigger);
      });
      observer.observe(trigger);
    }

    let currentPageNumber = 0;
    let pageNumberToFetch = 0;
    let loadedPageNumber = window.location.href.split('page=') || 1;
    loadedPageNumber = Number(loadedPageNumber[1]);
    if (Number.isNaN(loadedPageNumber)) {
      loadedPageNumber = 1;
    }
    // Array to store the pages that have already been loaded
    const pageNumbersLoaded = {[loadedPageNumber]: true};
    function getPage(sectionID, entry, sectionBind) {
      if (window.location.href.indexOf('page=') !== -1) {
        currentPageNumber = window.location.href.split('page=');
        // Check if load next or previous products
        if (entry.target.hasAttribute('data-infinite-load-next')) {
          if (Number(currentPageNumber[1]) === loadedPageNumber) {
            pageNumberToFetch = loadedPageNumber + 1;
          } else {
            pageNumberToFetch = Math.max(...Object.keys(pageNumbersLoaded)) + 1;
          }
        } else if (entry.target.hasAttribute('data-infinite-load-prev')) {
          if (Number(currentPageNumber[1]) === 1) return;
          if (Number(currentPageNumber[1]) === loadedPageNumber) {
            pageNumberToFetch = loadedPageNumber - 1;
          } else {
            pageNumberToFetch = Math.min(...Object.keys(pageNumbersLoaded)) - 1;
          }
        }
        if (pageNumberToFetch > window.paginationSize || pageNumberToFetch <= 0) {
          return;
        }
        const collectionPageToFetchUrl = window.location.href.replace(
          `page=${currentPageNumber[1]}`,
          `page=${pageNumberToFetch}`
        );
        const newUrl = `${collectionPageToFetchUrl}&section_id=${sectionID}`;
        if (newUrl) {
          appendNewListItems(newUrl, sectionBind, entry.target, pageNumberToFetch);
          history.pushState({}, '', collectionPageToFetchUrl);
          pageNumbersLoaded[pageNumberToFetch] = false;
        }
        // eslint-disable-next-line no-negated-condition
      } else if (window.location.href.indexOf('?') !== -1) {
        history.pushState({}, '', `${window.location.href}&page=1`);
        pageNumbersLoaded['1'] = false;
      } else {
        const newUrl = `${window.location.href}?page=2&section_id=${sectionID}`;
        if (entry.target.hasAttribute('data-infinite-load-next')) {
          history.pushState({}, '', `${window.location.href}?page=2`);
          appendNewListItems(newUrl, sectionBind, entry.target);
          pageNumbersLoaded['2'] = false;
        }
      }
    }

    /**
     * @param {string} pageUrl - URL of section to fetch
     * @param {element} trigger - element that triggers infinite scroll
     * @param {element} sectionBind - element that the new HTML will get bound to
     */
    async function appendNewListItems(pageUrl, sectionMount, trigger) {
      // show loading spinner
      if (trigger.hasAttribute('data-infinite-load-next')) {
        if (loadNextSpinner) {
          loadNextSpinner.classList.remove('hide');
        }
      } else if (trigger.hasAttribute('data-infinite-load-prev')) {
        if (loadPrevSpinner) {
          loadPrevSpinner.classList.remove('hide');
        }
      }

      if (!pageUrl && !sectionMount) {
        return;
      }
      try {
        const response = await fetch(pageUrl);
        if (!response.ok) throw new Error('Network response was not ok');
        const data = await response.text();
        const sectionHtml = new DOMParser().parseFromString(data, 'text/html');
        const newListItemsHtml = sectionHtml.body.querySelector('.collection-grid').innerHTML;

        if (trigger.hasAttribute('data-infinite-load-next')) {
          sectionMount.insertAdjacentHTML('beforeend', newListItemsHtml);
          // hide loading spinner
          if (loadNextSpinner) {
            loadNextSpinner.classList.add('hide');
          }
        } else if (trigger.hasAttribute('data-infinite-load-prev')) {
          const firstProdCard = document.querySelector('.collection-grid__tile .product-card a');
          sectionMount.insertAdjacentHTML('afterbegin', newListItemsHtml);
          // hide loading spinner
          if (loadPrevSpinner) {
            loadPrevSpinner.classList.add('hide');
          }
          if (adjustingSrollPosition === false) {
            retainScrollPosOnLoadPrev(firstProdCard);
          }
        }

        // attach product add to cart function to each product card after insertion
        let loadedPgNumber = window.location.href.split('page=') || 1;
        loadedPgNumber = Number(loadedPgNumber[1]);
        const newProductCards = document.querySelectorAll(`.product-card[data-page-number='${loadedPgNumber}']`);
        if (newProductCards && newProductCards.length > 0) {
          newProductCards.forEach((product) => ProductCard(product));

          const selectEls = document.querySelectorAll('[data-custom-select]');
          if (selectEls.length > 0) {
            selectEls.forEach((selectEl) => {
              if (selectEl.classList.contains('appear-in')) return;
              const newSelectEl = selectEl.querySelector('select');
              customSelect(newSelectEl);
              selectEl.classList.add('appear-in');
            });
          }
        }

        const observer = new IntersectionObserver((entries) => {
          if (!entries[0].isIntersecting) return;
          getPage(window.collectionSectionID, entries[0], sectionMount);
          observer.unobserve(entries[0].target);
        });

        // listen for intersects on new product cards
        const loadMoreTriggers = document.querySelectorAll('[data-infinite-load-next]');
        if (loadMoreTriggers) {
          const lastLoadMoreTrigger = loadMoreTriggers[loadMoreTriggers.length - 1];
          if (lastLoadMoreTrigger) observer.observe(lastLoadMoreTrigger);
        }

        const firstLoadPrevTrigger = document.querySelector('[data-infinite-load-prev]');
        if (firstLoadPrevTrigger) {
          if (firstLoadPrevTrigger) observer.observe(firstLoadPrevTrigger);
        }

        // Bind click event listener to new product cards
        const prodCards = document.querySelectorAll('.collection-grid__tile .product-card a');
        if (prodCards) scrollToSelectedProduct(prodCards);

        // Initialise new reviews snippets
        if (typeof window.ratingSnippet === 'function') {
          window.ratingSnippet('ruk_rating_snippet', {
            store: 'vida-glow',
            color: '#000',
            linebreak: false,
            text: 'Reviews',
            callback: () => {
              const snippet = document.querySelectorAll('.ruk-rating-snippet-count');
              for (let i = 0; i < snippet.length; i++) {
                let currentRating = snippet[i].innerHTML;
                currentRating = currentRating.replace('(', ' ').replace(')', '');
                snippet[i].innerHTML = currentRating;
              }
            },
          });
        }
      } catch (evt) {
        window.console.error(evt);
      }
    }

    function retainScrollPosOnLoadPrev(firstProdCard) {
      if (!firstProdCard) return;
      const productCard = firstProdCard.closest('.product-card');
      if (!productCard) return;
      setTimeout(() => {
        const offset = productCard.offsetTop - 100;
        window.scrollTo(0, offset);
      }, 100);
    }

    function scrollToSelectedProduct(prodCards) {
      if (!prodCards) return;
      // Check if url exists as key in local storage and scroll to that product
      const productIdToScrollTo = localStorage.getItem(window.location.href);
      if (productIdToScrollTo) {
        const productCard = document.querySelector(
          `.collection-grid .product-card[data-product-id="${productIdToScrollTo}"]`
        );
        if (productCard) {
          adjustingSrollPosition = true;
          window.addEventListener('load', function () {
            setTimeout(() => {
              const offset = productCard.offsetTop - 100;
              window.scrollTo(0, offset);
              adjustingSrollPosition = false;
            }, 100);
          });
          // Remove item from local storage
          localStorage.removeItem(window.location.href);
        }
      }
      // Set local storage item
      prodCards.forEach((prodCard) => {
        prodCard.addEventListener('click', () => {
          const productCard = prodCard.closest('.product-card');
          const productID = productCard.dataset.productId;
          // get current page number and push state
          let sectionUrl = window.location.href;
          if (productID) {
            if (window.location.href.indexOf('page=') > -1) {
              let currentPageNum = window.location.href.split('page=');
              currentPageNum = currentPageNum[1].charAt(0);
              const pageNumToSet = productCard.dataset.pageNumber;
              sectionUrl = window.location.href.replace(`page=${currentPageNum}`, `page=${pageNumToSet}`);
              history.pushState({}, '', sectionUrl);
            }
            localStorage.setItem(sectionUrl, productID);
          }
        });
      });
    }

    if (window.enableInfiniteScroll) {
      const prodCards = document.querySelectorAll('.collection-grid__tile .product-card a');
      scrollToSelectedProduct(prodCards);

      if (window.paginationSize > 1) {
        if (loadNextIntersect) handleAutoInfiniteScroll(window.collectionSectionID, loadNextIntersect, bindingSection);
        if (loadPrevIntersect) handleAutoInfiniteScroll(window.collectionSectionID, loadPrevIntersect, bindingSection);
      }
    }
  },
});
